import React, { useEffect, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
    Users, Home, MessageCircle, Image as ImageIcon, Calendar, MapPin, Euro,
    ArrowRightLeft, Mail, ShieldCheck, Trash2, Pin, PinOff, Search, Send,
    CheckCircle2, Circle, Sprout, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import AdminEditRoomDialog from "@/components/AdminEditRoomDialog";
import { formatPrice } from "@/lib/currency";

export default function Admin() {
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [matches, setMatches] = useState(null);
    const [reports, setReports] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("rooms");
    const [search, setSearch] = useState("");
    const [seeding, setSeeding] = useState(false);
    const [editingRoom, setEditingRoom] = useState(null);

    const loadAll = async () => {
        try {
            const [s, u, r, c] = await Promise.all([
                api.get("/admin/stats"),
                api.get("/admin/users"),
                api.get("/admin/rooms"),
                api.get("/admin/contacts"),
            ]);
            setStats(s.data);
            setUsers(u.data);
            setRooms(r.data);
            setContacts(c.data);
        } catch (e) {
            toast.error(e.response?.status === 403 ? "You're not an admin." : "Failed to load admin data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadAll(); }, []);

    const refresh = async () => {
        const [s, u, r, c] = await Promise.all([
            api.get("/admin/stats"),
            api.get("/admin/users"),
            api.get("/admin/rooms"),
            api.get("/admin/contacts"),
        ]);
        setStats(s.data); setUsers(u.data); setRooms(r.data); setContacts(c.data);
    };

    const togglePin = async (room) => {
        try {
            await api.patch(`/admin/rooms/${room.id}`, { is_pinned: !room.is_pinned });
            setRooms((prev) => prev.map((x) => x.id === room.id ? { ...x, is_pinned: !x.is_pinned } : x));
            toast.success(room.is_pinned ? "Unpinned" : "Pinned to top");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
        }
    };

    const toggleStatus = async (room) => {
        const next = room.status === "hidden" ? "active" : "hidden";
        try {
            await api.patch(`/admin/rooms/${room.id}`, { status: next });
            setRooms((prev) => prev.map((x) => x.id === room.id ? { ...x, status: next } : x));
            toast.success(next === "hidden" ? "Room hidden from Browse" : "Room re-activated");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
        }
    };

    const deleteRoom = async (roomId) => {
        try {
            await api.delete(`/admin/rooms/${roomId}`);
            setRooms((prev) => prev.filter((x) => x.id !== roomId));
            toast.success("Room deleted");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
        }
    };

    const deleteUser = async (userId) => {
        try {
            await api.delete(`/admin/users/${userId}`);
            setUsers((prev) => prev.filter((x) => x.id !== userId));
            setRooms((prev) => prev.filter((x) => x.owner_id !== userId));
            toast.success("User + all their data deleted");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
        }
    };

    const markContactRead = async (id, read) => {
        try {
            await api.patch(`/admin/contacts/${id}?read=${read}`);
            setContacts((prev) => prev.map((x) => x.id === id ? { ...x, read } : x));
        } catch { /* noop */ }
    };

    const deleteContact = async (id) => {
        try {
            await api.delete(`/admin/contacts/${id}`);
            setContacts((prev) => prev.filter((x) => x.id !== id));
            toast.success("Message deleted");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
        }
    };

    const seedRooms = async () => {
        setSeeding(true);
        try {
            const { data } = await api.post("/admin/seed-rooms");
            toast.success(`Seed complete: +${data.created_rooms} rooms, +${data.created_users} users (${data.skipped_existing} already there)`);
            await refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Seed failed");
        } finally {
            setSeeding(false);
        }
    };

    const backupRooms = async () => {
        try {
            const { data } = await api.get("/admin/backup-rooms");
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const date = new Date().toISOString().slice(0, 10);
            a.download = `erasmusswap-backup-${date}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            toast.success(`Backup downloaded (${data.rooms_count} rooms)`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Backup failed");
        }
    };

    const restoreRooms = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const text = await file.text();
            const payload = JSON.parse(text);
            if (!payload.rooms) throw new Error("This file is not a valid backup");
            const { data } = await api.post("/admin/restore-rooms", payload);
            toast.success(`Restored ${data.restored_rooms} rooms and ${data.restored_seed_users} users`);
            await refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Restore failed");
        } finally {
            e.target.value = "";
        }
    };

    const clearSeeds = null;

    if (loading) {
        return (
            <div className="min-h-[50vh] flex items-center justify-center" data-testid="admin-loading">
                <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
            </div>
        );
    }

    const unreadCount = contacts.filter((c) => !c.read).length;

    const statCards = stats ? [
        { label: "Users", value: stats.users, icon: Users },
        { label: "Rooms", value: stats.rooms, icon: Home },
        { label: "Messages", value: stats.messages, icon: MessageCircle },
        { label: "Photos", value: stats.files, icon: ImageIcon },
    ] : [];

    const q = search.trim().toLowerCase();
    const filteredRooms = q
        ? rooms.filter((r) =>
            (r.title || "").toLowerCase().includes(q) ||
            (r.city || "").toLowerCase().includes(q) ||
            (r.destination_city || "").toLowerCase().includes(q) ||
            (r.owner_name || "").toLowerCase().includes(q) ||
            (r.owner_email || "").toLowerCase().includes(q)
        )
        : rooms;
    const filteredUsers = q
        ? users.filter((u) =>
            (u.name || "").toLowerCase().includes(q) ||
            (u.email || "").toLowerCase().includes(q) ||
            (u.home_city || "").toLowerCase().includes(q)
        )
        : users;

    return (
        <div data-testid="admin-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5 text-primary" />
                </div>
                <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary">Admin</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold">Platform control</h1>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                {statCards.map((s) => (
                    <div key={s.label} data-testid={`admin-stat-${s.label.toLowerCase()}`}
                        className="bg-white border border-border/60 rounded-2xl p-5 flex items-center justify-between">
                        <div>
                            <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{s.label}</div>
                            <div className="font-display text-3xl font-bold mt-1">{s.value}</div>
                        </div>
                        <s.icon className="w-7 h-7 text-primary" />
                    </div>
                ))}
            </div>

            {/* Safety panel: backup + restore (seed button kept but de-emphasized) */}
            <div className="mt-6 bg-white border border-border/60 rounded-2xl p-5" data-testid="admin-safety-panel">
                <div className="flex items-start gap-3">
                    <Sprout className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold">Backup and restore</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                            Download a snapshot of every room (photos, texts, prices). Restore later if anything goes wrong. Strongly recommended before re-deploying or making bulk changes.
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2 items-center">
                            <Button
                                onClick={backupRooms}
                                data-testid="admin-backup-btn"
                                className="rounded-full bg-foreground hover:bg-foreground/90 text-background"
                            >
                                Download backup (.json)
                            </Button>
                            <label data-testid="admin-restore-label" className="cursor-pointer">
                                <input
                                    type="file"
                                    accept="application/json,.json"
                                    className="hidden"
                                    onChange={restoreRooms}
                                    data-testid="admin-restore-input"
                                />
                                <span className="inline-flex items-center px-5 py-2 rounded-full text-sm font-medium border border-border bg-white hover:bg-muted transition-colors">
                                    Restore from file
                                </span>
                            </label>
                            <div className="hidden md:block w-px h-6 bg-border mx-1" />
                            <Button
                                onClick={seedRooms}
                                disabled={seeding}
                                variant="ghost"
                                data-testid="admin-seed-btn"
                                className="rounded-full text-muted-foreground hover:text-foreground"
                                title="Adds 20 demo rooms only if they don't exist yet"
                            >
                                {seeding ? "Adding..." : "Add demo rooms (only if missing)"}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="-mx-4 sm:mx-0 px-4 sm:px-0 overflow-x-auto scrollbar-hide">
                    <div className="flex gap-2 whitespace-nowrap">
                        <button
                            data-testid="admin-tab-rooms"
                            onClick={() => setTab("rooms")}
                            className={`px-5 py-2 rounded-full text-sm font-medium transition-all shrink-0 ${tab === "rooms" ? "bg-foreground text-background" : "bg-muted text-foreground hover:bg-muted/70"}`}
                        >
                            Rooms ({rooms.length})
                        </button>
                        <button
                            data-testid="admin-tab-users"
                            onClick={() => setTab("users")}
                            className={`px-5 py-2 rounded-full text-sm font-medium transition-all shrink-0 ${tab === "users" ? "bg-foreground text-background" : "bg-muted text-foreground hover:bg-muted/70"}`}
                        >
                            Users ({users.length})
                        </button>
                        <button
                            data-testid="admin-tab-contacts"
                            onClick={() => setTab("contacts")}
                            className={`px-5 py-2 rounded-full text-sm font-medium transition-all relative shrink-0 ${tab === "contacts" ? "bg-foreground text-background" : "bg-muted text-foreground hover:bg-muted/70"}`}
                        >
                            Inbox ({contacts.length})
                            {unreadCount > 0 && (
                                <span className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold">
                                    {unreadCount}
                                </span>
                            )}
                        </button>
                        <button
                            data-testid="admin-tab-matches"
                            onClick={async () => {
                                setTab("matches");
                                if (!matches) {
                                    try {
                                        const { data } = await api.get("/admin/matches");
                                        setMatches(data);
                                    } catch { /* ignore */ }
                                }
                            }}
                            className={`px-5 py-2 rounded-full text-sm font-medium transition-all shrink-0 ${tab === "matches" ? "bg-foreground text-background" : "bg-muted text-foreground hover:bg-muted/70"}`}
                        >
                            Matches {matches ? `(${matches.totals.grand})` : ""}
                        </button>
                        <button
                            data-testid="admin-tab-reports"
                            onClick={async () => {
                                setTab("reports");
                                try {
                                    const { data } = await api.get("/admin/reports");
                                    setReports(data);
                                } catch { /* ignore */ }
                            }}
                            className={`px-5 py-2 rounded-full text-sm font-medium transition-all shrink-0 ${tab === "reports" ? "bg-foreground text-background" : "bg-muted text-foreground hover:bg-muted/70"}`}
                        >
                            Reports {reports ? `(${reports.filter(r => r.status === "pending").length})` : ""}
                        </button>
                    </div>
                </div>
                {tab !== "contacts" && tab !== "matches" && tab !== "reports" && (
                    <div className="sm:ml-auto relative sm:max-w-xs w-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={`Search ${tab}…`}
                            data-testid="admin-search-input"
                            className="pl-9 rounded-full h-10"
                        />
                    </div>
                )}
            </div>

            {tab === "rooms" && (
                <div className="mt-6 space-y-4" data-testid="admin-rooms-list">
                    {filteredRooms.length === 0 ? (
                        <div className="border border-dashed border-border rounded-2xl p-12 text-center text-muted-foreground">
                            {q ? "No rooms match your search." : "No rooms yet."}
                        </div>
                    ) : filteredRooms.map((r) => (
                        <div key={r.id} data-testid={`admin-room-${r.id}`}
                            className={`bg-white border rounded-2xl p-5 transition-colors ${r.is_pinned ? "border-primary/60 ring-1 ring-primary/20" : "border-border/60"}`}>
                            <div className="flex flex-wrap justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <h3 className="font-display text-lg font-semibold">{r.title}</h3>
                                        {r.is_pinned && (
                                            <span className="text-[10px] font-bold uppercase tracking-wider bg-primary text-primary-foreground px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                                                <Pin className="w-3 h-3" /> Pinned
                                            </span>
                                        )}
                                        {r.status === "hidden" && (
                                            <span className="text-[10px] font-bold uppercase tracking-wider bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                                                Hidden
                                            </span>
                                        )}
                                        {r.is_seed && (
                                            <span className="text-[10px] font-bold uppercase tracking-wider bg-accent text-accent-foreground px-2 py-0.5 rounded-full">
                                                Demo
                                            </span>
                                        )}
                                        {(!r.photos || r.photos.length === 0) && (
                                            <span className="text-[10px] font-bold uppercase tracking-wider bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                                                No photos
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-sm text-muted-foreground mt-1 flex flex-wrap gap-x-3 gap-y-1">
                                        <span><MapPin className="w-3 h-3 inline" /> {r.city}, {r.country}</span>
                                        <span><Euro className="w-3 h-3 inline" /> {formatPrice(r.rent_per_month, r.currency || "EUR", { perMonth: true })}</span>
                                        <span><Calendar className="w-3 h-3 inline" /> {r.available_from} → {r.available_to}</span>
                                        {r.destination_city && (
                                            <span><ArrowRightLeft className="w-3 h-3 inline" /> going to {r.destination_city}</span>
                                        )}
                                    </div>
                                    <div className="text-sm text-muted-foreground mt-1">
                                        By <span className="font-medium text-foreground">{r.owner_name}</span> · {r.owner_email}
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2 items-start">
                                    <Button
                                        variant="outline" size="sm"
                                        onClick={() => setEditingRoom(r)}
                                        data-testid={`admin-edit-room-${r.id}`}
                                        className="rounded-full"
                                    >
                                        <Pencil className="w-3.5 h-3.5" />
                                        <span className="ml-1">Edit</span>
                                    </Button>
                                    <Button
                                        variant="outline" size="sm"
                                        onClick={() => togglePin(r)}
                                        data-testid={`admin-pin-${r.id}`}
                                        className="rounded-full"
                                        title={r.is_pinned ? "Unpin" : "Pin to top"}
                                    >
                                        {r.is_pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                                        <span className="ml-1">{r.is_pinned ? "Unpin" : "Pin"}</span>
                                    </Button>
                                    <Button
                                        variant="outline" size="sm"
                                        onClick={() => toggleStatus(r)}
                                        data-testid={`admin-hide-${r.id}`}
                                        className="rounded-full"
                                    >
                                        {r.status === "hidden" ? "Show" : "Hide"}
                                    </Button>
                                    <a
                                        href={`/rooms/${r.id}`}
                                        target="_blank"
                                        rel="noopener"
                                        className="text-xs text-primary hover:underline py-2"
                                        data-testid={`admin-view-room-${r.id}`}
                                    >
                                        View →
                                    </a>
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                            <Button variant="outline" size="sm" data-testid={`admin-delete-room-${r.id}`} className="rounded-full text-destructive border-destructive/40 hover:bg-destructive hover:text-white">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle>Delete this room?</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                    "{r.title}" will be permanently removed. The owner's account stays intact.
                                                </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                <AlertDialogAction onClick={() => deleteRoom(r.id)} className="bg-destructive hover:bg-destructive/90">Delete room</AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                </div>
                            </div>

                            {r.description && (
                                <p className="text-sm text-foreground/80 mt-3 leading-relaxed whitespace-pre-wrap line-clamp-2">
                                    {r.description}
                                </p>
                            )}

                            {r.photos && r.photos.length > 0 && (
                                <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2 mt-4">
                                    {r.photos.slice(0, 6).map((p, i) => (
                                        <a key={i} href={fileUrl(p)} target="_blank" rel="noopener"
                                            className="relative aspect-square rounded-xl overflow-hidden bg-muted border border-border">
                                            <img
                                                src={fileUrl(p)}
                                                alt=""
                                                className="w-full h-full object-cover transition-transform hover:scale-105"
                                                onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
                                            />
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {tab === "users" && (
                <div className="mt-6 bg-white border border-border/60 rounded-2xl overflow-hidden" data-testid="admin-users-list">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-muted">
                                <tr className="text-left">
                                    <th className="px-4 py-3 font-semibold">Name</th>
                                    <th className="px-4 py-3 font-semibold">Email</th>
                                    <th className="px-4 py-3 font-semibold">Home city</th>
                                    <th className="px-4 py-3 font-semibold">University</th>
                                    <th className="px-4 py-3 font-semibold">Refs</th>
                                    <th className="px-4 py-3 font-semibold">Joined</th>
                                    <th className="px-4 py-3 font-semibold"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.length === 0 ? (
                                    <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">{q ? "No users match." : "No users."}</td></tr>
                                ) : filteredUsers.map((u) => (
                                    <tr key={u.id} data-testid={`admin-user-${u.id}`} className="border-t border-border/40 hover:bg-muted/30">
                                        <td className="px-4 py-3 font-medium">
                                            {u.name}
                                            {u.is_seed && (
                                                <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-accent text-accent-foreground px-1.5 py-0.5 rounded-full">Demo</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <a href={`mailto:${u.email}`} className="text-primary hover:underline flex items-center gap-1">
                                                <Mail className="w-3 h-3" />{u.email}
                                            </a>
                                        </td>
                                        <td className="px-4 py-3">{u.home_city || "-"}</td>
                                        <td className="px-4 py-3">{u.home_university || "-"}</td>
                                        <td className="px-4 py-3">{u.referrals_count || 0}</td>
                                        <td className="px-4 py-3 text-muted-foreground">{(u.created_at || "").slice(0, 10)}</td>
                                        <td className="px-4 py-3 text-right">
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button variant="ghost" size="sm" data-testid={`admin-delete-user-${u.id}`} className="rounded-full text-destructive hover:bg-destructive hover:text-white">
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <AlertDialogTitle>Delete {u.name}?</AlertDialogTitle>
                                                        <AlertDialogDescription>
                                                            This removes the user, all their rooms, messages and uploaded files. Cannot be undone.
                                                        </AlertDialogDescription>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                        <AlertDialogAction onClick={() => deleteUser(u.id)} className="bg-destructive hover:bg-destructive/90">Delete user</AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {tab === "contacts" && (
                <div className="mt-6 space-y-3" data-testid="admin-contacts-list">
                    {contacts.length === 0 ? (
                        <div className="border border-dashed border-border rounded-2xl p-12 text-center text-muted-foreground">
                            No messages yet.
                        </div>
                    ) : contacts.map((c) => (
                        <div key={c.id} data-testid={`admin-contact-${c.id}`}
                            className={`bg-white border rounded-2xl p-5 ${c.read ? "border-border/60" : "border-primary/50 ring-1 ring-primary/20"}`}>
                            <div className="flex flex-wrap justify-between gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <div className="font-semibold">{c.name}</div>
                                        <a href={`mailto:${c.email}?subject=Re: ${encodeURIComponent(c.subject || "Your message")}`}
                                            className="text-primary hover:underline text-sm inline-flex items-center gap-1">
                                            <Mail className="w-3.5 h-3.5" /> {c.email}
                                        </a>
                                        {!c.read && (
                                            <span className="text-[10px] font-bold uppercase tracking-wider bg-primary text-primary-foreground px-2 py-0.5 rounded-full">New</span>
                                        )}
                                    </div>
                                    {c.subject && <div className="mt-1 text-sm font-medium">{c.subject}</div>}
                                    <p className="mt-2 text-sm text-foreground/90 whitespace-pre-wrap">{c.message}</p>
                                    <div className="text-xs text-muted-foreground mt-2">{(c.created_at || "").replace("T", " ").slice(0, 16)}</div>
                                </div>
                                <div className="flex flex-col gap-2 items-end shrink-0">
                                    <a
                                        href={`mailto:${c.email}?subject=Re: ${encodeURIComponent(c.subject || "Your message")}`}
                                        data-testid={`admin-contact-reply-${c.id}`}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90"
                                    >
                                        <Send className="w-3 h-3" /> Reply
                                    </a>
                                    <button
                                        onClick={() => markContactRead(c.id, !c.read)}
                                        data-testid={`admin-contact-toggle-${c.id}`}
                                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                                    >
                                        {c.read ? <Circle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
                                        {c.read ? "Mark unread" : "Mark read"}
                                    </button>
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                            <button data-testid={`admin-contact-delete-${c.id}`} className="inline-flex items-center gap-1 text-xs text-destructive hover:underline">
                                                <Trash2 className="w-3 h-3" /> Delete
                                            </button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle>Delete this message?</AlertDialogTitle>
                                                <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                <AlertDialogAction onClick={() => deleteContact(c.id)} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {tab === "matches" && (
                <div className="mt-6 space-y-6" data-testid="admin-matches-panel">
                    {!matches ? (
                        <div className="border border-dashed border-border rounded-2xl p-12 text-center text-muted-foreground">
                            Loading matches…
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="bg-white border border-border/60 rounded-2xl p-5">
                                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Total</div>
                                    <div className="font-display text-3xl font-bold mt-1">{matches.totals.grand}</div>
                                </div>
                                <div className="bg-white border border-border/60 rounded-2xl p-5">
                                    <div className="text-xs font-bold uppercase tracking-wider text-primary">1-on-1</div>
                                    <div className="font-display text-3xl font-bold mt-1">{matches.totals.mirrors}</div>
                                </div>
                                <div className="bg-white border border-border/60 rounded-2xl p-5">
                                    <div className="text-xs font-bold uppercase tracking-wider text-secondary">Chain (3-way)</div>
                                    <div className="font-display text-3xl font-bold mt-1">{matches.totals.chains}</div>
                                </div>
                                <div className="bg-white border border-border/60 rounded-2xl p-5">
                                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Buddies</div>
                                    <div className="font-display text-3xl font-bold mt-1">{matches.totals.buddies}</div>
                                </div>
                            </div>

                            <MatchTable
                                title="1-on-1 mirror matches"
                                testid="admin-matches-mirrors"
                                columns={["User A", "City A", "User B", "City B"]}
                                rows={matches.mirrors.map((m) => [
                                    `${m.user_a} (${m.email_a})`,
                                    m.city_a,
                                    `${m.user_b} (${m.email_b})`,
                                    m.city_b,
                                ])}
                            />
                            <MatchTable
                                title="Chain matches (3-way)"
                                testid="admin-matches-chains"
                                columns={["User A", "→", "User B", "→", "User C"]}
                                rows={matches.chains.map((c) => [
                                    `${c.user_a} (${c.city_a})`,
                                    "→",
                                    `${c.user_b} (${c.city_b})`,
                                    "→",
                                    `${c.user_c} (${c.city_c})`,
                                ])}
                            />
                            <MatchTable
                                title="Exchange Buddy matches"
                                testid="admin-matches-buddies"
                                columns={["User A", "User B", "Shared cities"]}
                                rows={matches.buddies.map((b) => [
                                    `${b.user_a} (${b.email_a})`,
                                    `${b.user_b} (${b.email_b})`,
                                    (b.shared_cities || []).join(", "),
                                ])}
                            />
                        </>
                    )}
                </div>
            )}

            {tab === "reports" && (
                <div className="mt-6" data-testid="admin-reports-list">
                    {!reports ? (
                        <div className="border border-dashed border-border rounded-2xl p-12 text-center text-muted-foreground">
                            Loading reports…
                        </div>
                    ) : reports.length === 0 ? (
                        <div className="border border-dashed border-border rounded-2xl p-12 text-center text-muted-foreground">
                            No reports yet.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {reports.map((r) => (
                                <div
                                    key={r.id}
                                    data-testid={`admin-report-${r.id}`}
                                    className={`bg-white border rounded-2xl p-4 sm:p-5 ${r.status === "pending" ? "border-primary/40" : "border-border/60 opacity-80"}`}
                                >
                                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary bg-primary/10 rounded-full px-3 py-1">
                                            {r.status}
                                        </div>
                                        <div className="text-xs text-muted-foreground">{r.created_at?.slice(0, 19).replace("T", " ")}</div>
                                    </div>
                                    <div className="font-display text-base font-semibold">
                                        {r.reason} — {r.target_type} {r.target_snapshot?.title ? `("${r.target_snapshot.title}")` : ""}
                                    </div>
                                    <div className="text-sm text-muted-foreground mt-1 break-words">
                                        <strong>By:</strong> {r.reporter_name || r.reporter_id} → <strong>User:</strong> {r.reported_user?.name || r.reported_user_id || "?"}
                                    </div>
                                    {r.description && (
                                        <div className="mt-2 text-sm bg-muted/40 rounded-xl p-3 whitespace-pre-wrap break-words">{r.description}</div>
                                    )}
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {r.target_type === "room" && r.target_id && (
                                            <Button
                                                variant="outline" size="sm"
                                                data-testid={`admin-report-remove-content-${r.id}`}
                                                onClick={async () => {
                                                    if (!window.confirm("Remove this listing permanently?")) return;
                                                    try {
                                                        await api.delete(`/admin/rooms/${r.target_id}`);
                                                        await api.put(`/admin/reports/${r.id}`, { status: "resolved", admin_action: "content_removed" });
                                                        toast.success("Listing removed and report resolved.");
                                                        const { data } = await api.get("/admin/reports");
                                                        setReports(data);
                                                    } catch { toast.error("Failed"); }
                                                }}
                                                className="rounded-full"
                                            >
                                                Remove listing
                                            </Button>
                                        )}
                                        {r.reported_user_id && (
                                            <Button
                                                variant="outline" size="sm"
                                                data-testid={`admin-report-suspend-${r.id}`}
                                                onClick={async () => {
                                                    if (!window.confirm("Suspend this user? Their listings will be hidden.")) return;
                                                    try {
                                                        await api.post(`/admin/users/${r.reported_user_id}/suspend?suspended=true`);
                                                        await api.put(`/admin/reports/${r.id}`, { status: "resolved", admin_action: "user_suspended" });
                                                        toast.success("User suspended and report resolved.");
                                                        const { data } = await api.get("/admin/reports");
                                                        setReports(data);
                                                    } catch { toast.error("Failed"); }
                                                }}
                                                className="rounded-full text-destructive border-destructive/60"
                                            >
                                                Suspend user
                                            </Button>
                                        )}
                                        {r.status === "pending" && (
                                            <Button
                                                variant="ghost" size="sm"
                                                data-testid={`admin-report-dismiss-${r.id}`}
                                                onClick={async () => {
                                                    try {
                                                        await api.put(`/admin/reports/${r.id}`, { status: "dismissed", admin_action: "no_action" });
                                                        const { data } = await api.get("/admin/reports");
                                                        setReports(data);
                                                    } catch { toast.error("Failed"); }
                                                }}
                                                className="rounded-full"
                                            >
                                                Dismiss
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            <div className="mt-10 flex gap-2">
                <Button variant="outline" className="rounded-full" onClick={refresh} data-testid="admin-refresh-btn">
                    Refresh data
                </Button>
            </div>

            {editingRoom && (
                <AdminEditRoomDialog
                    room={editingRoom}
                    open={!!editingRoom}
                    onOpenChange={(o) => { if (!o) setEditingRoom(null); }}
                    onSaved={(updated) => {
                        setRooms((prev) => prev.map((x) => x.id === updated.id ? updated : x));
                        setEditingRoom(null);
                    }}
                />
            )}
        </div>
    );
}

function MatchTable({ title, testid, columns, rows }) {
    return (
        <div className="bg-white border border-border/60 rounded-2xl overflow-hidden" data-testid={testid}>
            <div className="px-5 py-3 border-b border-border/60 flex items-baseline justify-between">
                <div className="font-display font-semibold">{title}</div>
                <div className="text-xs text-muted-foreground">{rows.length} {rows.length === 1 ? "match" : "matches"}</div>
            </div>
            {rows.length === 0 ? (
                <div className="px-5 py-6 text-sm text-muted-foreground">None yet.</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[520px]">
                        <thead className="bg-muted text-muted-foreground">
                            <tr>
                                {columns.map((c, i) => <th key={i} className="text-left px-5 py-2 font-medium whitespace-nowrap">{c}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, i) => (
                                <tr key={i} className="border-t border-border/50">
                                    {row.map((cell, j) => <td key={j} className="px-5 py-2 align-top whitespace-nowrap">{cell}</td>)}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
